import { Card } from './Card';
import { Sparkline } from './Sparkline';
import { v, font, financialToneColor } from '@/theme/tokens';
import { financialTone, formatChange, isMissing, NO_DATA } from '@/utils/format';

interface KPICardProps {
  label: string;
  value: string;
  /** MoM change as a ratio (0.042 = +4.2%), or percentage points for a margin
   * KPI — pass `changeFormatter` in that case so it renders as "+1,4 п.п.". */
  change?: number | null;
  /** Defaults to formatChange (relative %). Pass formatPoints for margins. */
  changeFormatter?: (value: number | null | undefined) => string;
  /** For cost metrics a decrease is good. */
  invertChange?: boolean;
  /** Shorthand alias for invertChange. */
  invert?: boolean;
  series?: (number | null)[];
  color?: string;
  caption?: string;
  /** The number behind `value`, so the headline figure itself can follow the
   * project-wide rule: negative is always the danger colour; positive is
   * only coloured green when `positiveIsGood` — a profit or the residual,
   * never an expense amount. Omit to leave the value in the neutral colour. */
  rawValue?: number | null;
  positiveIsGood?: boolean;
  /** Marks the card semantically, without changing its geometry: it keeps the
   * same size, padding, radius and type scale as every other KPI card and
   * only gains a border in the value's tone. Used for «Остаток», which must
   * stand out by state rather than by being a different-sized block. */
  emphasize?: boolean;
}

export function KPICard({
  label,
  value,
  change = null,
  changeFormatter = formatChange,
  invertChange = false,
  invert = false,
  series,
  color,
  caption = 'к прошлому месяцу',
  rawValue,
  positiveIsGood = false,
  emphasize = false,
}: KPICardProps) {
  const stroke = color ?? v('accent');
  const inverted = invertChange || invert;
  const good = isMissing(change) ? null : inverted ? change < 0 : change >= 0;
  const changeColor = good === null ? v('ink-muted') : good ? v('positive') : v('negative');
  const valueColor =
    rawValue === undefined ? v('ink') : financialToneColor(financialTone(rawValue, positiveIsGood));

  return (
    <Card
      padding={18}
      style={emphasize ? { border: `1.5px solid ${valueColor}` } : undefined}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 10,
        }}
      >
        <div style={{ fontSize: 12.5, color: v('ink-muted'), fontWeight: 600 }}>{label}</div>
        {series && series.length > 1 && <Sparkline data={series} color={stroke} />}
      </div>

      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          fontFamily: font.mono,
          letterSpacing: -0.5,
          lineHeight: 1.1,
          color: valueColor,
        }}
      >
        {value}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <span style={{ color: changeColor, fontSize: 12.5, fontWeight: 600, fontFamily: font.mono }}>
          {isMissing(change) ? NO_DATA : changeFormatter(change)}
        </span>
        <span style={{ fontSize: 11.5, color: v('ink-muted') }}>{caption}</span>
      </div>
    </Card>
  );
}
